import { ApexOptions } from 'apexcharts'
import { fNumber } from 'common/utils/formatNumber'
import {
  useCostCategoriesList,
  useGetTransactionPieChart,
  useGetTransactionStackChart,
} from 'modules/cost/api/queries'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FONT_PRIMARY } from 'theme/typography'

import {
  Box,
  Card,
  CardContent,
  Grid,
  InputLabel,
  Select,
  Typography,
  MenuItem,
  FormControl,
} from '@mui/material'
import { useTheme } from '@mui/material'

import DonutChart from './DonutChart'
import StackedChart from './StackedChart'

type Item = { label: string; value: number; color: string; percent: number }

const CostCard: React.FC = () => {
  const { t } = useTranslation('cost')
  const theme = useTheme()

  const [selectedCategory, setSelectedCategory] = React.useState<string>('all')

  const { data: categoriesData, isLoading: categoriesLoading } = useCostCategoriesList()

  const { data: transactionPieChartData, isLoading: pieLoading } = useGetTransactionPieChart(
    selectedCategory === 'all' ? '' : selectedCategory
  )
  const { data: transactionStackChartData, isInitialLoading: stackLoading } = useGetTransactionStackChart()

  const apiList: any[] = React.useMemo(() => {
    if (!transactionPieChartData) return []
    if (Array.isArray(transactionPieChartData)) return transactionPieChartData
    if (Array.isArray(transactionPieChartData.cost)) return transactionPieChartData.cost
    return []
  }, [transactionPieChartData])

  const paletteColors = React.useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.secondary.main,
    ],
    [theme]
  )

  const getColor = (idx: number, provided?: string) =>
    provided || paletteColors[idx % paletteColors.length]

  const items: Item[] = React.useMemo(() => {
    const total = apiList.reduce((sum, it) => sum + Number(it.total_amount ?? it.value ?? 0), 0)
    return apiList.map((it, idx) => {
      const value = Number(it.total_amount ?? it.value ?? 0)
      return {
        label: it.name ?? it.label ?? `Item ${idx + 1}`,
        value,
        color: getColor(idx, it.color),
        percent: total ? value / total * 100 : 0,
      }
    })
  }, [apiList, paletteColors])

  const totalValue = items.reduce((sum, it) => sum + it.value, 0)

  const donutSeries = items.map((it) => it.value)
  const donutOptions: ApexOptions = {
    chart: {
      type: 'donut',
      toolbar: { show: false },
      fontFamily: FONT_PRIMARY,
    },
    labels: items.map((it) => it.label),
    colors: items.map((it) => it.color),
    dataLabels: { enabled: false },
    legend: { show: false },
    plotOptions: { pie: { donut: { size: '85%' } } },
    tooltip: {
      y: {
        formatter: (value: any, opts?: any) => {
          const seriesIndex = opts?.seriesIndex ?? 0
          const item = items[seriesIndex] ?? { label: '', percent: 0 }
          const percent = item.percent ?? 0
          return `${fNumber(percent)}% = ${fNumber(Number(value))} ${t('unit.baht')}`
        },
      },
    },
  }

  return (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography mb={2} fontSize={18} fontWeight={600}>
            {t('cost.title')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box border={1} borderColor="grey.300" borderRadius={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography mb={2} fontSize={16} fontWeight={600}>
                  {t('summary.cost.title')}
                </Typography>
                <FormControl size="small">
                  <InputLabel>{t('summary.cost.select')}</InputLabel>
                  <Select
                    value={selectedCategory}
                    label={t('summary.cost.select')}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    disabled={categoriesLoading}
                  >
                    <MenuItem value="all">{t('summary.cost.select')}</MenuItem>
                    {categoriesData?.map((opt, i) => (
                      <MenuItem key={i} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <DonutChart
                items={items}
                totalValue={totalValue}
                donutOptions={donutOptions}
                donutSeries={donutSeries}
                t={t}
                loading={pieLoading}
              />
            </CardContent>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box border={1} borderColor="grey.300" borderRadius={2} height="100%">
            <CardContent>
              <Typography fontWeight={600} variant="subtitle1" gutterBottom>
                {t('chart.cost.title')}
              </Typography>

              <StackedChart
                data={transactionStackChartData}
                getColor={getColor}
                paletteColors={paletteColors}
                t={t}
                loading={stackLoading}
              />
            </CardContent>
          </Box>
        </Grid>
      </Grid>
    </Card>
  )
}

export default CostCard
